import type {AppMarketConfig} from '@smart/configs';
import type {PaymentConfigServiceV1} from '@smart/payment-config-service';
import type {PriceTypeV1} from '@smart/price-preferences-provider';
import {MARKET, type MarketId, SalesChannel} from '@smart/types';
import {getLeasingLabelForSpecificMarket} from '@smart/utils';
import type {
  AppModel,
  DisclaimerRefs,
} from '../../bff/app-model/create-app-model';
import type {
  Highlight,
  ProductSearchProduct,
} from '../../bff/product-search-model/create-product-search-model';
import type {Badge, FeatureAppConfig} from '../../bff/types';

type StockLevelText = {
  stockText: string;
  deliveryText?: string;
};

export type ProductCardProps = {
  id: string;
  imageUrl: string;
  brandName: string;
  headline: string;
  stockLevelStatus: 'inStock' | 'outOfStock';
  stockLevelText: StockLevelText;
  leasingPrice: string;
  calculatedLeasingPrice?: string;
  netLeasingPrice: string;
  calculatedNetLeasingPrice?: string;
  defaultTotalPriceLabel: string;
  adjustedTotalPriceLabel: string;
  totalPrice: string;
  modelYear: string;
  totalPriceRaw: number;
  calculatedTotalPrice?: string;
  netPrice: string;
  calculatedNetPrice?: string;
  paymentButtonText: string;
  buyButtonUrl: string;
  buyButtonText: string;
  moreInformationButtonUrl: string;
  moreInformationButtonText: string;
  highlights: Highlight[];
  addToGarageFeatureAppConfig?: FeatureAppConfig;
  disclaimerRefs: DisclaimerRefs;
  legalDisclaimer: string;
  paymentConfigService: PaymentConfigServiceV1;
  exclVatLabel: string;
  inclVatLabel: string;
  cashPriceAdditionalInformation: string;
  energyLabelImage: string | undefined;
  index: number;
  productType: string;
  highlightExteriorColor: string;
  highlightInteriorColor: string;
  appMarketConfig: AppMarketConfig;
  leasingLabel: string;
  leasingPriceReference: string;
  isDemoVehicleMode: boolean;
  showLeasingTools?: boolean;
  isEnergyLabelModalEnabled?: boolean;
  badges: Badge[];
};

const getStockLevelText = (
  isInStock: boolean,
  stockLevelInStockText: string,
  stockLevelOutOfStockText: string,
  estimatedDeliveryText: string,
  earliestBeautifiedLeadTime?: string,
): StockLevelText => {
  if (!isInStock) {
    return {stockText: stockLevelOutOfStockText};
  }

  if (earliestBeautifiedLeadTime) {
    return {
      stockText: estimatedDeliveryText.replace(`{{days}}`, ``).slice(0, -1),
      deliveryText: earliestBeautifiedLeadTime,
    };
  }

  return {stockText: stockLevelInStockText};
};

interface ProductCardPropsArgs {
  item: ProductSearchProduct;
  appModel: AppModel;
  appMarketConfig: AppMarketConfig;
  index: number;
  paymentConfigService: PaymentConfigServiceV1;
  priceType: PriceTypeV1;
  marketId: MarketId;
  showLeasingTools: boolean;
  isEnergyLabelModalEnabled: boolean;
  salesChannel: SalesChannel;
}

export const createProductCardProps = ({
  item,
  appModel,
  appMarketConfig,
  index,
  paymentConfigService,
  priceType,
  marketId,
  showLeasingTools,
  isEnergyLabelModalEnabled,
  salesChannel,
}: ProductCardPropsArgs): ProductCardProps => {
  const isInStock = item.stockLevelStatus === `inStock`;

  const stockLevelText = getStockLevelText(
    isInStock,
    appModel.stockLevelInStockText,
    appModel.stockLevelOutOfStockText,
    appModel.estimatedDeliveryText,
    item.earliestBeautifiedLeadTime,
  );

  const buyButtonText =
    item.stockLevelStatus === `inStock`
      ? salesChannel === SalesChannel.DEMO
        ? appModel.demoBuyButtonText
        : appModel.buyButtonText
      : appModel.stockLevelOutOfStockText;

  //NOTE: buyButtonDemoVehicleLink only exists in GB market
  const buyButtonUrl =
    marketId.toUpperCase() === MARKET.GB && salesChannel === SalesChannel.DEMO
      ? appModel.buyButtonDemoVehicleLink
      : appModel.buyButtonPageLink;

  const addToGarageFeatureAppConfig =
    appModel.addToGarageFeatureAppConfig && item.productId
      ? {
          ...appModel.addToGarageFeatureAppConfig,
          config: {productId: item.productId},
        }
      : undefined;

  let legalDisclaimer = appModel.legalDisclaimer;
  Object.entries(item.legalDisclaimerValues).forEach(([key, value]) => {
    legalDisclaimer = legalDisclaimer.replace(key, value);
  });

  const leasingLabel = getLeasingLabelForSpecificMarket({
    marketId: marketId as MarketId,
    paymentMode: paymentConfigService.getConfig()?.mode,
    priceType,
    labels: {
      leasePriceLabelPCP: appModel.adjustedPcpLeasingPriceLabel,
      leasePriceLabelSME: appModel.leasingPriceLabelSME,
      leasePriceLabel: appModel.leasingPriceLabel,
    },
  });

  return {
    id: item.productId,
    imageUrl: item.imageUrl,
    brandName: item.brandName,
    headline: item.headline,
    stockLevelStatus:
      item.stockLevelStatus as ProductCardProps['stockLevelStatus'],
    leasingPrice: item.leasingPrice,
    calculatedLeasingPrice: item.calculatedLeasingPrice,
    totalPrice: item.totalPrice,
    modelYear: item.modelYear,
    totalPriceRaw: item.totalPriceRaw,
    calculatedTotalPrice: item.calculatedTotalPrice,
    highlights: item.highlights,
    disclaimerRefs: appModel.disclaimerRefs,
    stockLevelText,
    defaultTotalPriceLabel: appModel.defaultTotalPriceLabel,
    adjustedTotalPriceLabel: appModel.adjustedTotalPriceLabel,
    paymentButtonText: appModel.paymentButtonText,
    buyButtonText,
    buyButtonUrl,
    moreInformationButtonText: appModel.moreInformationButtonText,
    moreInformationButtonUrl: appModel.moreInformationButtonPageLink,
    addToGarageFeatureAppConfig,
    legalDisclaimer,
    exclVatLabel: appModel.exclVatLabel,
    inclVatLabel: appModel.inclVatLabel,
    cashPriceAdditionalInformation:
      item.brandName === `smart #3`
        ? ``
        : appModel.cashPriceAdditionalInformation,
    netPrice: item.netPrice,
    calculatedNetPrice: item.calculatedNetPrice,
    netLeasingPrice: item.netLeasingPrice,
    calculatedNetLeasingPrice: item.calculatedNetLeasingPrice,
    energyLabelImage: appModel.energyLabelImage,
    productType: item.productType,
    highlightExteriorColor: appModel.highlightExteriorColor,
    highlightInteriorColor: appModel.highlightInteriorColor,
    paymentConfigService,
    appMarketConfig,
    leasingLabel,
    leasingPriceReference: appModel.leasingPriceReference,
    index,
    showLeasingTools,
    isEnergyLabelModalEnabled,
    badges: item.badges,
    isDemoVehicleMode: salesChannel === SalesChannel.DEMO,
  };
};
